import { attemptedQuestion, isAttemptedOutcome } from '../practice/practiceProgress';
import { sectionFor } from '../practice/practiceSections';
import { topicName } from '../practice/practiceModel';

const DAY = 86400000;
const sections = ['A', 'B', 'C'];
export function coachToday(now = Date.now()) {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(now));
    const value = type => parts.find(part => part.type === type)?.value;
    return `${value('year')}-${value('month')}-${value('day')}`;
}
export function coachDateValid(date) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '')) return false;
    const parsed = new Date(`${date}T00:00:00Z`);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date;
}
export const coachDayNumber = date => coachDateValid(date) ? Date.parse(`${date}T00:00:00Z`) / DAY : null;
export function shiftCoachDate(date, offset) {
    const number = coachDayNumber(date);
    return number === null ? '' : new Date((number + offset) * DAY).toISOString().slice(0, 10);
}
export function coachWeekStart(date) {
    if (!coachDateValid(date)) return '';
    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
    return shiftCoachDate(date, -((weekday + 6) % 7));
}
export function coachDateLabel(date, options = { day: 'numeric', month: 'short', year: 'numeric' }) {
    return coachDateValid(date) ? new Date(`${date}T12:00:00Z`).toLocaleDateString('en-GB', { timeZone: 'UTC', ...options }) : '';
}
export function currentCoachQuestions(questions = []) {
    return [...new Map(questions.filter(q => q?.id && q.status !== 'draft' && q.recordKind !== 'scenario').map(q => [q.id, q])).values()];
}
export function examTargets(questions, progress = {}, examDate, today = coachToday()) {
    const bank = currentCoachQuestions(questions);
    const valid = coachDateValid(examDate) && coachDateValid(today);
    const days = valid ? coachDayNumber(examDate) - coachDayNumber(today) : null;
    const status = days === null ? 'unset' : days > 0 ? 'future' : days === 0 ? 'today' : 'past';
    const bySection = sections.map(section => {
        const items = bank.filter(q => sectionFor(q) === section);
        const remaining = items.filter(q => !attemptedQuestion(progress, q.id));
        return { section, total: items.length, attempted: items.length - remaining.length, remaining: remaining.length,
            perDay: status === 'future' ? Math.ceil(remaining.length / days) : null,
            unavailable: remaining.filter(q => q.contentUnavailable).length };
    });
    return { status, daysUntil: days, studyDays: status === 'future' ? days : 0, sections: bySection,
        total: bank.length, remaining: bySection.reduce((total, item) => total + item.remaining, 0),
        unavailable: bySection.reduce((total, item) => total + item.unavailable, 0) };
}
export function revisionQueue(questions, progress = {}, learning = {}, today = coachToday()) {
    const records = learning.questions || {};
    const queue = [], improved = [];
    currentCoachQuestions(questions).forEach(question => {
        const state = records[question.id] || {};
        const outcome = progress.results?.[question.id]?.outcome;
        if (outcome === 'correct' && (state.incorrectAttempts > 0 || state.previousOutcome === 'incorrect')) improved.push(question);
        const reasons = [];
        if (state.confidence === 'need-help') reasons.push('You asked for help');
        if (outcome === 'incorrect') reasons.push('Last answer incorrect');
        if (state.confidence === 'unsure') reasons.push('You marked this unsure');
        if (!reasons.length) return;
        const lastAttemptAt = Number(state.lastAttemptAt || 0);
        const lastDate = lastAttemptAt > 0 && Number.isFinite(lastAttemptAt) ? coachToday(lastAttemptAt) : '';
        const dueDate = state.confidence === 'need-help' || !lastDate ? today : shiftCoachDate(lastDate, outcome === 'incorrect' ? 1 : 2);
        queue.push({ question, reasons, dueDate, due: dueDate <= today, attempts: Math.max(0, Number(state.attempts) || 0),
            priority: state.confidence === 'need-help' ? 0 : outcome === 'incorrect' ? 1 : 2 });
    });
    queue.sort((a, b) => Number(b.due) - Number(a.due) || a.priority - b.priority || a.dueDate.localeCompare(b.dueDate) || Number(a.question.practiceNumber || 0) - Number(b.question.practiceNumber || 0));
    return { items: queue, due: queue.filter(item => item.due), upcoming: queue.filter(item => !item.due), improved };
}
function planItems(plan, byId, progress) {
    const ids = [...new Set(Array.isArray(plan.questionIds) ? plan.questionIds : [])];
    const completed = ids.filter(id => isAttemptedOutcome(progress?.results?.[id]?.outcome));
    const pendingIds = ids.filter(id => !completed.includes(id));
    return { ids, completed, pendingIds, questions: ids.map(id => byId.get(id)).filter(Boolean),
        remaining: pendingIds.map(id => byId.get(id)).filter(q => q && !q.contentUnavailable),
        unavailable: pendingIds.filter(id => !byId.has(id) || byId.get(id).contentUnavailable) };
}
export function missedPlans(plans = [], dayProgress = {}, questions = [], today = coachToday()) {
    const byId = new Map(currentCoachQuestions(questions).map(q => [q.id, q]));
    return plans.filter(plan => coachDateValid(plan.date) && plan.date < today).map(plan => ({ ...plan, ...planItems(plan, byId, dayProgress[plan.date]) }))
        .filter(plan => plan.pendingIds.length).sort((a, b) => a.date.localeCompare(b.date));
}
export function estimatedQuestionMinutes(question, settings = {}) {
    const section = sectionFor(question);
    const configured = Number(settings.sectionMinutes?.[section]);
    return Number.isFinite(configured) && configured > 0 ? configured : section === 'C' ? 30 : 3;
}
export function weeklyPlans(plans = [], dayProgress = {}, questions = [], anchor = coachToday(), settings = {}) {
    const start = coachWeekStart(anchor);
    const byId = new Map(currentCoachQuestions(questions).map(q => [q.id, q]));
    const byDate = new Map(plans.map(plan => [plan.date, plan]));
    return Array.from({ length: 7 }, (_, offset) => {
        const date = shiftCoachDate(start, offset);
        const plan = byDate.get(date) || { date, title: 'No questions assigned', questionIds: [] };
        const summary = planItems(plan, byId, dayProgress[date]);
        return { ...plan, ...summary, date, minutes: Math.ceil(summary.questions.reduce((sum, q) => sum + estimatedQuestionMinutes(q, settings), 0)),
            remainingMinutes: Math.ceil(summary.remaining.reduce((sum, q) => sum + estimatedQuestionMinutes(q, settings), 0)),
            sections: Object.fromEntries(sections.map(section => [section, summary.questions.filter(q => sectionFor(q) === section).length])) };
    });
}
export function topicMastery(questions = [], progress = {}, learning = {}) {
    const groups = new Map();
    currentCoachQuestions(questions).forEach(question => {
        const name = topicName(question);
        if (!groups.has(name)) groups.set(name, { name, questions: [], total: 0, attempted: 0, correct: 0, incorrect: 0, reviewed: 0, unsure: 0, help: 0 });
        const group = groups.get(name), outcome = progress.results?.[question.id]?.outcome, state = learning.questions?.[question.id];
        group.questions.push(question); group.total++;
        if (attemptedQuestion(progress, question.id)) group.attempted++;
        if (outcome === 'correct') group.correct++;
        if (outcome === 'incorrect') group.incorrect++;
        if (outcome === 'reviewed') group.reviewed++;
        if (state?.confidence === 'unsure') group.unsure++;
        if (state?.confidence === 'need-help') group.help++;
    });
    return [...groups.values()].map(group => ({ ...group, remaining: group.total - group.attempted,
        accuracy: group.correct + group.incorrect ? Math.round(group.correct / (group.correct + group.incorrect) * 100) : null,
        next: group.questions.find(q => !q.contentUnavailable && (progress.results?.[q.id]?.outcome === 'incorrect' || learning.questions?.[q.id]?.confidence === 'need-help')) || group.questions.find(q => !q.contentUnavailable && !attemptedQuestion(progress, q.id)) || group.questions.find(q => !q.contentUnavailable) }))
        .sort((a, b) => b.help - a.help || b.incorrect - a.incorrect || (a.accuracy ?? 101) - (b.accuracy ?? 101) || a.name.localeCompare(b.name));
}
function activityCount(entry) {
    if (Array.isArray(entry?.questionIds)) return new Set(entry.questionIds.filter(id => typeof id === 'string' && id)).size;
    return Number.isFinite(entry?.count) ? Math.max(0, Math.floor(entry.count)) : 0;
}
export function practiceActivity(activity = {}, today = coachToday()) {
    const counts = Object.fromEntries(Object.entries(activity).filter(([date]) => coachDateValid(date) && date <= today).map(([date, value]) => [date, activityCount(value)]));
    const weekStart = coachWeekStart(today), lastDay = shiftCoachDate(weekStart, 6);
    const heatStart = shiftCoachDate(weekStart, -77);
    const heatmap = Array.from({ length: 84 }, (_, index) => { const date = shiftCoachDate(heatStart, index); return { date, count: counts[date] || 0, future: date > today }; });
    let cursor = counts[today] > 0 ? today : shiftCoachDate(today, -1), streak = 0;
    while (counts[cursor] > 0) { streak++; cursor = shiftCoachDate(cursor, -1); }
    let bestStreak = 0, run = 0, previous = '';
    Object.keys(counts).filter(date => counts[date] > 0).sort().forEach(date => { run = previous && shiftCoachDate(previous, 1) === date ? run + 1 : 1; bestStreak = Math.max(bestStreak, run); previous = date; });
    const weekCount = Object.entries(counts).filter(([date]) => date >= weekStart && date <= lastDay).reduce((sum, [, value]) => sum + value, 0);
    return { streak, streakCapped: streak > 0 && cursor < shiftCoachDate(today, -83), bestStreak, weekCount, activeDays: Object.values(counts).filter(value => value > 0).length, heatmap, weekStart, lastDay };
}
